import React from "react";
import styled from "styled-components";
import {
  Modal,
  Descriptions,
  Tag,
  Button,
  Space,
  Divider,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Switch,
} from "antd";
import { getUnitDisplayAddress } from "../../lib/unitAddressUtils";
import { useNavigate } from "react-router-dom";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  HomeOutlined,
  DollarOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  MessageOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  BankOutlined,
  CarOutlined,
  ToolOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;

const DetailsModal = ({
  open,
  onCancel,
  onSave,
  title,
  icon,
  data,
  type,
  loading = false,
  isEditing = false,
  onToggleEdit,
  form,
  actionButtons = [],
  customFields = [],
  showActions = true,
  relatedData = {},
}) => {
  const [internalForm] = Form.useForm();
  const currentForm = form || internalForm;
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "active":
      case "completed":
      case "occupied":
        return "success";
      case "pending":
      case "in progress":
      case "scheduled":
        return "processing";
      case "overdue":
      case "expired":
      case "vacant":
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "active":
      case "completed":
      case "occupied":
        return <CheckCircleOutlined />;
      case "pending":
      case "in progress":
      case "scheduled":
        return <ClockCircleOutlined />;
      case "overdue":
      case "expired":
      case "vacant":
      case "failed":
        return <ExclamationCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const dateObj = date?.toDate?.() || new Date(date);
    return dateObj.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatPaymentMethod = (method) => {
    if (!method) return "N/A";

    const capitalized =
      method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();

    if (method.toLowerCase() === "ach") return "ACH";
    if (method.toLowerCase() === "check") return "Check";
    if (method.toLowerCase() === "cash") return "Cash";
    if (method.toLowerCase() === "credit") return "Credit";
    if (method.toLowerCase() === "debit") return "Debit";
    if (method.toLowerCase() === "wire") return "Wire";
    if (method.toLowerCase() === "zelle") return "Zelle";
    if (method.toLowerCase() === "venmo") return "Venmo";
    if (method.toLowerCase() === "paypal") return "PayPal";

    return capitalized;
  };

  const getFieldIcon = (fieldName) => {
    const iconMap = {
      name: <UserOutlined />,
      email: <MailOutlined />,
      phone: <PhoneOutlined />,
      unit: <HomeOutlined />,
      amount: <DollarOutlined />,
      dueDate: <CalendarOutlined />,
      status: <CheckCircleOutlined />,
      paymentMethod: <CreditCardOutlined />,
      address: <HomeOutlined />,
      rentAmount: <DollarOutlined />,
      monthlyRent: <DollarOutlined />,
      tenantId: <UserOutlined />,
      unitId: <HomeOutlined />,
      notes: <FileTextOutlined />,
      id: <IdcardOutlined />,
      createdAt: <CalendarOutlined />,
      updatedAt: <CalendarOutlined />,
      paidDate: <CalendarOutlined />,
      transactionId: <BankOutlined />,
      stripeConnectIdOverride: <BankOutlined />,
      repairAutoApproveLimit: <DollarOutlined />,
      vehicleInfo: <CarOutlined />,
      emergencyContact: <PhoneOutlined />,
      maintenance: <ToolOutlined />,
    };
    return iconMap[fieldName] || <FileTextOutlined />;
  };

  const renderField = (field, value) => {
    if (isEditing && field.editable !== false) {
      return renderEditableField(field, value);
    }
    return renderDisplayField(field, value);
  };

  const renderDisplayField = (field, value) => {
    const {
      name,
      label,
      type: fieldType,
      format,
      customRender,
      displayValue,
    } = field;
    const displayVal = displayValue !== undefined ? displayValue : value;

    // Use custom render if provided
    if (customRender !== undefined && customRender !== null) {
      return customRender;
    }

    if (format === "status") {
      return (
        <Tag
          color={getStatusColor(displayVal)}
          icon={getStatusIcon(displayVal)}
        >
          {displayVal
            ? displayVal.charAt(0).toUpperCase() + displayVal.slice(1)
            : "N/A"}
        </Tag>
      );
    }

    if (format === "date") {
      return formatDate(displayVal);
    }

    if (format === "currency") {
      return `$${displayVal?.toLocaleString() || "0"}`;
    }

    if (format === "paymentMethod") {
      return formatPaymentMethod(displayVal);
    }

    if (format === "code") {
      return (
        <code style={{ fontSize: "0.8rem", color: "#666" }}>{displayVal}</code>
      );
    }

    if (field.type === "switch") {
      return displayVal ? "Yes" : "No";
    }

    return displayVal || "N/A";
  };

  const renderEditableField = (field, value) => {
    const { name, label, type: fieldType, options, placeholder } = field;

    switch (fieldType) {
      case "text":
        return (
          <Form.Item name={name} rules={field.rules || []}>
            <Input placeholder={placeholder || `Enter ${label}`} />
          </Form.Item>
        );

      case "textarea":
        return (
          <Form.Item name={name} rules={field.rules || []}>
            <TextArea rows={4} placeholder={placeholder || `Enter ${label}`} />
          </Form.Item>
        );

      case "number":
        return (
          <Form.Item name={name} rules={field.rules || []}>
            <InputNumber
              style={{ width: "100%" }}
              placeholder={placeholder || `Enter ${label}`}
              formatter={field.formatter}
              parser={field.parser}
            />
          </Form.Item>
        );

      case "select":
        return (
          <Form.Item name={name} rules={field.rules || []}>
            <Select placeholder={placeholder || `Select ${label}`}>
              {options?.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );

      case "date":
        return (
          <Form.Item name={name} rules={field.rules || []}>
            <DatePicker
              style={{ width: "100%" }}
              placeholder={placeholder || `Select ${label}`}
            />
          </Form.Item>
        );

      case "switch":
        return (
          <Form.Item
            name={name}
            valuePropName="checked"
            rules={field.rules || []}
          >
            <Switch />
          </Form.Item>
        );

      default:
        return (
          <Form.Item name={name} rules={field.rules || []}>
            <Input placeholder={placeholder || `Enter ${label}`} />
          </Form.Item>
        );
    }
  };

  const renderFields = () => {
    const allFields = [...customFields];

    // Add default fields based on type
    if (type === "payment") {
      const tenant = relatedData.tenants?.find((t) => t.id === data?.tenantId);
      const unit = relatedData.units?.find((u) => u.id === data?.unitId);

      // Format unit address for payment details
      let unitDisplay = "Unknown Door";
      if (unit) {
        unitDisplay = getUnitDisplayAddress(unit) || unit.name || unitDisplay;
      }

      // Calculate final total including adjustments
      const baseAmount = data?.amount || 0;
      const adjustments = data?.adjustments || [];
      const adjustmentTotal = adjustments.reduce(
        (sum, adj) => sum + (adj.amount || 0),
        0
      );
      const finalTotal = baseAmount + adjustmentTotal;

      allFields.push(
        {
          name: "tenantId",
          label: "Tenant",
          value: tenant?.name || "Unknown Tenant",
          icon: <UserOutlined />,
          editable: false,
        },
        {
          name: "unitId",
          label: "Door",
          value: unitDisplay,
          icon: <HomeOutlined />,
          editable: false,
        },
        {
          name: "amount",
          label: "Base Amount",
          format: "currency",
          editable: false,
        },
        {
          name: "finalTotal",
          label: "Total Due",
          value: finalTotal,
          format: "currency",
          editable: false,
          customRender:
            adjustments.length > 0 ? (
              <div>
                <div style={{ fontWeight: 500 }}>
                  $
                  {finalTotal.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "12px",
                    color: "#8c8c8c",
                  }}
                >
                  <div style={{ marginBottom: "4px" }}>Adjustments:</div>
                  {adjustments.map((adj, idx) => (
                    <div
                      key={idx}
                      style={{ marginLeft: "8px", marginBottom: "2px" }}
                    >
                      • {adj.description}: {adj.amount >= 0 ? "+" : ""}$
                      {adj.amount.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ) : null,
        },
        { name: "dueDate", label: "Due Date", format: "date", editable: false },
        { name: "status", label: "Status", format: "status", editable: false }
      );

      if (data?.status === "paid") {
        allFields.push(
          {
            name: "paidDate",
            label: "Paid Date",
            format: "date",
            editable: false,
          },
          {
            name: "method",
            label: "Payment Method",
            format: "paymentMethod",
            editable: false,
          },
          { name: "transactionId", label: "Transaction ID", editable: false }
        );
      }
    }

    if (type === "tenant") {
      const unit = relatedData.units?.find((u) => u.id === data?.unitId);

      // Format unit address
      let unitDisplay = "Unknown Door";
      if (unit) {
        unitDisplay = getUnitDisplayAddress(unit) || unit.name || unitDisplay;
      }

      // Use monthlyRent if rentAmount is not available (for backward compatibility)
      const rentAmount =
        data?.monthlyRent !== undefined ? data.monthlyRent : data?.rentAmount;

      allFields.push(
        { name: "name", label: "Name", type: "text", editable: true },
        { name: "email", label: "Email", type: "text", editable: true },
        { name: "phone", label: "Phone", type: "text", editable: true },
        {
          name: "unitId",
          label: "Door",
          value: unitDisplay,
          icon: <HomeOutlined />,
          editable: false,
        },
        { name: "status", label: "Status", format: "status", editable: false },
        {
          name: "monthlyRent",
          label: "Rent Amount",
          value: rentAmount,
          format: "currency",
          editable: true,
        },
        {
          name: "rentDueDay",
          label: "Rent Due Day",
          type: "number",
          editable: true,
        }
      );
    }

    if (type === "unit") {
      const tenant = relatedData.tenants?.find((t) => t.id === data?.tenantId);

      // Use monthlyRent if rentAmount is not available (for backward compatibility)
      const rentAmount =
        data?.monthlyRent !== undefined ? data.monthlyRent : data?.rentAmount;

      allFields.push(
        { name: "name", label: "Door Name", type: "text", editable: true },
        {
          name: "address",
          label: "Address",
          type: "textarea",
          editable: true,
          value: getUnitDisplayAddress(data) || data?.address,
        },
        {
          name: "monthlyRent",
          label: "Rent Amount",
          value: rentAmount,
          format: "currency",
          editable: true,
        },
        { name: "status", label: "Status", format: "status", editable: false },
        {
          name: "tenantId",
          label: "Tenant",
          value: tenant?.name || "No Tenant",
          icon: <UserOutlined />,
          editable: false,
        }
      );
    }

    if (type === "property") {
      const depositOptions = relatedData.depositAccountOptions ?? [
        { value: "", label: "Primary account" },
      ];
      const depositValue =
        data?.stripeConnectIdOverride != null &&
        data?.stripeConnectIdOverride !== ""
          ? data.stripeConnectIdOverride
          : "";
      const depositLabel =
        depositOptions.find((o) => o.value === depositValue)?.label ??
        "Primary account";
      allFields.push(
        {
          name: "address",
          label: "Address",
          value: data?.address ?? data?.id,
          icon: <HomeOutlined />,
          editable: false,
        },
        {
          name: "repairAutoApproveLimit",
          label: "AI Auto-Approve Limit ($)",
          type: "number",
          value: data?.repairAutoApproveLimit,
          editable: true,
        },
        {
          name: "emailMaintenanceTicketDetailsToVendors",
          label:
            "Email vendors maintenance ticket details (no tenant chat access)",
          type: "switch",
          value: data?.emailMaintenanceTicketDetailsToVendors === true,
          editable: true,
        },
        {
          name: "stripeConnectIdOverride",
          label: "Rent deposits to",
          type: "select",
          value: depositValue,
          options: depositOptions,
          editable: true,
          displayValue: depositLabel,
        },
        {
          name: "_depositConfirmation",
          label: " ",
          value: `Rent for this property will be deposited to: ${depositLabel}`,
          editable: false,
          type: "static",
          span: 2,
        }
      );
    }

    return allFields.map((field, index) => {
      const value =
        field.value !== undefined ? field.value : data?.[field.name];
      const fieldIcon = field.icon || getFieldIcon(field.name);

      return (
        <Descriptions.Item
          key={field.name || index}
          label={
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {fieldIcon}
              {field.label}
            </span>
          }
          span={field.span || 1}
        >
          {isEditing
            ? renderEditableField(field, value)
            : renderDisplayField(field, value)}
        </Descriptions.Item>
      );
    });
  };

  const renderFooterButtons = () => {
    const buttons = [];

    if (showActions) {
      if (isEditing) {
        buttons.push(
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={() => {
              currentForm.validateFields().then((values) => {
                onSave?.(values);
              });
            }}
          >
            Save Changes
          </Button>
        );
        buttons.push(
          <Button
            key="cancel-edit"
            icon={<CloseOutlined />}
            onClick={onToggleEdit}
          >
            Cancel Edit
          </Button>
        );
      } else {
        buttons.push(
          <Button key="edit" icon={<EditOutlined />} onClick={onToggleEdit}>
            Edit
          </Button>
        );
      }
    }

    // Add "Go to Chat" button for tenant type
    if (type === "tenant" && data?.id) {
      buttons.push(
        <Button
          key="go-to-chat"
          type="primary"
          icon={
            <MessageOutlined style={{ filter: "brightness(0) invert(1)" }} />
          }
          onClick={() => {
            navigate(`/communication/${data.id}`);
            onCancel(); // Close the modal
          }}
        >
          Go to Chat
        </Button>
      );
    }

    // Add custom action buttons
    actionButtons.forEach((button, index) => {
      buttons.push(
        <Button
          key={`action-${index}`}
          type={button.type || "default"}
          icon={button.icon}
          onClick={button.onClick}
          loading={button.loading}
          danger={button.danger}
        >
          {button.text}
        </Button>
      );
    });

    buttons.push(
      <Button key="close" onClick={onCancel}>
        Close
      </Button>
    );

    return buttons;
  };

  return (
    <StyledModal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {icon}
          <span style={{ color: "white" }}>{title}</span>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={renderFooterButtons()}
      width={window.innerWidth <= 768 ? "95vw" : 600}
      centered
      destroyOnClose
    >
      {isEditing ? (
        <Form
          form={currentForm}
          layout="vertical"
          initialValues={
            type === "property" && data
              ? {
                  ...data,
                  stripeConnectIdOverride: data.stripeConnectIdOverride ?? "",
                  emailMaintenanceTicketDetailsToVendors:
                    data.emailMaintenanceTicketDetailsToVendors === true,
                }
              : data
          }
          preserve={false}
        >
          <Descriptions column={1} bordered size="small">
            {renderFields()}
          </Descriptions>
        </Form>
      ) : (
        <Descriptions column={1} bordered size="small">
          {renderFields()}
        </Descriptions>
      )}

      <Divider
        style={{ borderColor: "rgba(0, 0, 0, 0.1)", margin: "16px 0" }}
      />

      <div
        style={{
          textAlign: "center",
          color: "rgba(0, 0, 0, 0.6)",
          fontSize: "0.85rem",
          padding: "8px 0",
          lineHeight: "1.4",
        }}
      >
        Created: {formatDate(data?.createdAt)}
        {data?.updatedAt && data.updatedAt !== data.createdAt && (
          <span style={{ display: "block", marginTop: "4px" }}>
            Updated: {formatDate(data.updatedAt)}
          </span>
        )}
      </div>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  --lo-modal-header-height: 56px;
  --lo-modal-close-size: 32px;

  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 52px 16px 20px;
    min-height: var(--lo-modal-header-height);
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: calc(
      (var(--lo-modal-header-height) - var(--lo-modal-close-size)) / 2
    ) !important;
    right: 16px;
    background: transparent !important;
    border-radius: 0 !important;
    width: var(--lo-modal-close-size) !important;
    height: var(--lo-modal-close-size) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;

    &:hover {
      color: white !important;
      background: transparent !important;
      border-color: transparent !important;
    }
  }

  .ant-modal-close .ant-modal-close-x,
  .ant-modal-close .ant-modal-close-icon,
  .ant-modal-close .anticon,
  .ant-modal-close svg,
  .ant-modal-close:hover .ant-modal-close-x,
  .ant-modal-close:hover .ant-modal-close-icon,
  .ant-modal-close:hover .anticon,
  .ant-modal-close:hover svg {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  .ant-modal-body {
    padding: 24px;
    background: #ffffff;
    overflow-x: hidden;
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0px;
  }

  .ant-modal {
    max-width: 90vw;
    margin: 20px auto;
  }

  .ant-descriptions-item-label {
    font-weight: 600;
    color: var(--color-text) !important;
    background: rgba(0, 0, 0, 0.05) !important;
    border-color: rgba(0, 0, 0, 0.1) !important;

    .anticon {
      color: var(--color-text-muted) !important;
    }
  }

  .ant-descriptions-item-content {
    color: var(--color-text) !important;
    background: rgba(0, 0, 0, 0.02) !important;
    border-color: rgba(0, 0, 0, 0.1) !important;
  }

  /* Failed/error status tag: red background with black text (match payment table) */
  .ant-descriptions-item-content .ant-tag.ant-tag-error {
    background: #fecaca !important;
    border: 1px solid #f87171 !important;
    color: #1f2937 !important;
  }
  .ant-descriptions-item-content .ant-tag.ant-tag-error .anticon {
    color: #1f2937 !important;
  }

  .ant-descriptions-bordered .ant-descriptions-item {
    border-color: rgba(0, 0, 0, 0.1) !important;
  }

  .ant-modal-footer {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding: 16px 24px;
    background: #ffffff;
    display: flex;
    flex-wrap: wrap;
    gap: 10px 12px; /* row-gap, column-gap */
    justify-content: center;
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-descriptions-item-content .ant-form-item {
    margin-bottom: 0;
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    --lo-modal-header-height: 48px;
    --lo-modal-close-size: 28px;

    .ant-modal {
      max-width: 95vw;
      margin: 10px auto;
    }

    .ant-modal-body {
      padding: 16px;
    }

    .ant-modal-header {
      padding: 12px 48px 12px 16px;
      min-height: var(--lo-modal-header-height);
    }

    .ant-modal-title {
      font-size: 16px;
      color: white !important;

      /* Ensure icon is white on mobile */
      .anticon {
        color: white !important;
      }
    }

    .ant-modal-close {
      top: calc(
        (var(--lo-modal-header-height) - var(--lo-modal-close-size)) / 2
      ) !important;
      right: 16px !important;
      font-size: 14px;
      color: white !important;
      background: transparent !important;
      border-radius: 0 !important;
      width: var(--lo-modal-close-size) !important;
      height: var(--lo-modal-close-size) !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      border: none !important;
      position: absolute !important;

      &:hover {
        color: white !important;
        background: transparent !important;
        border-color: transparent !important;
      }
    }

    .ant-modal-footer {
      padding: 12px 16px;
      display: flex;
      flex-wrap: wrap;
      gap: 10px 10px; /* ensure spacing between wrapped rows */

      .ant-btn {
        margin: 0 0 8px 0 !important;
        width: 100%;
        height: 44px;
        font-size: 15px;
        font-weight: 500;

        &:last-child {
          margin-bottom: 0 !important;
        }
      }
    }

    /* Improve footer content styling on mobile */
    .ant-modal-body > div:last-child {
      font-size: 0.8rem !important;
      padding: 6px 0 !important;
      line-height: 1.3 !important;
    }

    /* Improve descriptions layout on mobile */
    .ant-descriptions {
      .ant-descriptions-item-label {
        font-size: 13px;
        padding: 8px 12px;
        width: 35% !important;
        min-width: 100px;
      }

      .ant-descriptions-item-content {
        font-size: 14px;
        padding: 8px 12px;
        width: 65% !important;
        word-break: break-word;
      }

      .ant-descriptions-item {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        min-height: 44px;
      }
    }
  }

  @media (max-width: 480px) {
    --lo-modal-header-height: 44px;
    --lo-modal-close-size: 26px;

    .ant-modal {
      max-width: 98vw;
      margin: 5px auto;
    }

    .ant-modal-body {
      padding: 12px;
    }

    .ant-modal-header {
      padding: 10px 44px 10px 12px;
      min-height: var(--lo-modal-header-height);
    }

    .ant-modal-title {
      font-size: 15px;
      color: white !important;

      /* Ensure icon is white on mobile */
      .anticon {
        color: white !important;
      }
    }

    .ant-modal-close {
      top: calc(
        (var(--lo-modal-header-height) - var(--lo-modal-close-size)) / 2
      ) !important;
      right: 12px !important;
      font-size: 12px;
      color: white !important;
      background: transparent !important;
      border-radius: 0 !important;
      width: var(--lo-modal-close-size) !important;
      height: var(--lo-modal-close-size) !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      border: none !important;
      position: absolute !important;

      &:hover {
        color: white !important;
        background: transparent !important;
        border-color: transparent !important;
      }
    }

    .ant-modal-footer {
      padding: 10px 12px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px 8px;

      .ant-btn {
        margin: 0 0 6px 0 !important;
        width: 100%;
        font-size: 14px;
        height: 42px;
        font-weight: 500;

        &:last-child {
          margin-bottom: 0 !important;
        }
      }
    }

    /* Improve footer content styling on small mobile */
    .ant-modal-body > div:last-child {
      font-size: 0.75rem !important;
      padding: 4px 0 !important;
      line-height: 1.2 !important;
    }

    /* Improve descriptions layout on small mobile */
    .ant-descriptions {
      .ant-descriptions-item-label {
        font-size: 12px;
        padding: 6px 10px;
        width: 40% !important;
        min-width: 80px;
      }

      .ant-descriptions-item-content {
        font-size: 13px;
        padding: 6px 10px;
        width: 60% !important;
        word-break: break-word;
        line-height: 1.4;
      }

      .ant-descriptions-item {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        min-height: 40px;
      }
    }
  }
`;

export default DetailsModal;
